import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader, Globe } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Языковые настройки
const LANGUAGES = {
  ru: { name: 'Русский', flag: '🇷🇺' },
  en: { name: 'English', flag: '🇬🇧' },
  kk: { name: 'Қазақша', flag: '🇰🇿' }
};

// Переводы интерфейса
const UI_TRANSLATIONS = {
  ru: {
    backToThesaurus: 'Вернуться к тезаурусу',
    conceptId: 'Идентификатор концепта',
    otherLanguages: 'Другие языки',
    definition: 'Определение',
    scopeNote: 'Примечание',
    topTerms: 'Главные термины',
    preferredTerm: 'Предпочтительный термин',
    useFor: 'Используйте вместо',
    broaderTerms: 'Широкие термины',
    narrowerTerms: 'Узкие термины',
    relatedTerms: 'Связанные термины',
    usedFor: 'Используется для',
    lexicalEquivalent: 'Лексические эквиваленты',
    microthesaurus: 'Микротезаурус',
    notFound: 'Концепт не найден'
  },
  en: {
    backToThesaurus: 'Back to thesaurus',
    conceptId: 'Concept ID',
    otherLanguages: 'Other languages',
    definition: 'Definition',
    scopeNote: 'Scope Note',
    topTerms: 'Top Terms',
    preferredTerm: 'Preferred Term',
    useFor: 'Use For',
    broaderTerms: 'Broader Terms',
    narrowerTerms: 'Narrower Terms',
    relatedTerms: 'Related Terms',
    usedFor: 'Used For',
    lexicalEquivalent: 'Lexical Equivalents',
    microthesaurus: 'Microthesaurus',
    notFound: 'Concept not found'
  },
  kk: {
    backToThesaurus: 'Тезаурусқа оралу',
    conceptId: 'Концепт идентификаторы',
    otherLanguages: 'Басқа тілдер',
    definition: 'Анықтама',
    scopeNote: 'Ескертпе',
    topTerms: 'Басты терминдер',
    preferredTerm: 'Қолайлы термин',
    useFor: 'Орнына қолданылады',
    broaderTerms: 'Кең терминдер',
    narrowerTerms: 'Тар терминдер',
    relatedTerms: 'Байланысты терминдер',
    usedFor: 'Үшін қолданылады',
    lexicalEquivalent: 'Лексикалық эквиваленттер',
    microthesaurus: 'Микротезаурус',
    notFound: 'Концепт табылмады'
  }
};

const ConceptPage = () => {
  const { conceptId } = useParams();
  const navigate = useNavigate();
  const [term, setTerm] = useState(null);
  const [allTerms, setAllTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState('ru');

  // Переводы для текущего языка
  const t = UI_TRANSLATIONS[language];

  useEffect(() => {
    const fetchConcept = async () => {
      try {
        const response = await axios.get(`${API}/thesaurus/${conceptId}`);
        setTerm(response.data);
      } catch (err) {
        console.error('Error fetching concept:', err);
        setError('Concept not found');
      } finally {
        setLoading(false);
      }
    };

    const fetchAllTerms = async () => {
      try {
        const response = await axios.get(`${API}/thesaurus`);
        setAllTerms(response.data);
      } catch (err) {
        console.error('Error fetching all terms:', err);
      }
    };

    fetchConcept();
    fetchAllTerms();
  }, [conceptId]);

  // Найти ID термина по имени
  const findTermIdByName = (termName) => {
    const found = allTerms.find(t => 
      t.term === termName || 
      t.synonyms?.includes(termName) ||
      (t.pt && Object.values(t.pt).includes(termName))
    );
    return found?.id;
  };

  // Получить название термина на выбранном языке
  const getTermName = (termData) => {
    if (termData.pt && termData.pt[language]) {
      return termData.pt[language];
    }
    if (termData.pt && termData.pt.ru) {
      return termData.pt.ru;
    }
    return termData.term;
  };

  // Получить определение на выбранном языке
  const getDefinition = (termData) => {
    if (termData.definition && typeof termData.definition === 'object') {
      return termData.definition[language] || termData.definition.ru || null;
    }
    return termData.definition || termData.description;
  };

  // Получить scope note на выбранном языке
  const getScopeNote = (termData) => {
    if (termData.sn && typeof termData.sn === 'object') {
      return termData.sn[language] || termData.sn.ru || null;
    }
    return termData.scope_note;
  };

  // Рендер кликабельной ссылки на термин
  const renderTermLink = (termName, index) => {
    const termId = findTermIdByName(termName);
    
    if (termId) {
      return (
        <Link
          key={index}
          to={`/concept/${termId}`}
          className="text-blue-600 hover:text-blue-800 hover:underline inline-block"
        >
          {termName}
        </Link>
      );
    }
    
    return (
      <span key={index} className="text-gray-800">
        {termName}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !term) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || t.notFound}</p>
          <button
            onClick={() => navigate('/thesaurus')}
            className="text-blue-600 hover:underline"
          >
            {t.backToThesaurus}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header with language selector */}
      <div className="border-b border-gray-300">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/thesaurus')}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.backToThesaurus}
            </button>
            
            {/* Language Switcher */}
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-1">
              {Object.entries(LANGUAGES).map(([code, lang]) => (
                <button
                  key={code}
                  onClick={() => setLanguage(code)}
                  className={`px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2 ${
                    language === code
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`lang-${code}`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span className="hidden sm:inline">{lang.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8 max-w-5xl">
        {/* Concept ID */}
        <div className="text-sm text-gray-500 mb-2">
          {t.conceptId}: {term.id}
        </div>

        {/* Preferred Term */}
        <h1 className="text-4xl font-bold text-gray-900 mb-6">
          {getTermName(term)}
        </h1>

        {/* Other Languages - показать варианты на других языках */}
        {(term.pt || term.language_variants) && (
          <div className="mb-8 bg-gray-50 border-l-4 border-blue-500 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5" />
              {t.otherLanguages}
            </h2>
            <div className="space-y-2">
              {Object.entries(LANGUAGES).map(([code, lang]) => {
                if (code === language) return null;
                const termInLang = term.pt?.[code] || term.language_variants?.[code];
                if (!termInLang) return null;
                return (
                  <div key={code} className="flex gap-3">
                    <span className="font-semibold text-gray-700 w-32 flex items-center gap-2">
                      <span>{lang.flag}</span> {lang.name}:
                    </span>
                    <span className="text-gray-900">{termInLang}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Definition */}
        {getDefinition(term) && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-3">{t.definition}</h2>
            <p className="text-gray-800 text-lg leading-relaxed border-l-4 border-gray-300 pl-4">
              {getDefinition(term)}
            </p>
          </div>
        )}

        {/* Scope Note */}
        {getScopeNote(term) && (
          <div className="mb-8 bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <h2 className="text-lg font-bold text-gray-900 mb-2">{t.scopeNote}</h2>
            <p className="text-gray-800">{getScopeNote(term)}</p>
          </div>
        )}

        {/* UF - Use For (Synonyms) */}
        {term.synonyms && term.synonyms.length > 0 && (
          <div className="mb-8 border-t border-gray-300 pt-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">
              <span className="text-blue-600">UF</span> — {t.useFor}
            </h2>
            <div className="pl-6 space-y-2">
              {term.synonyms.map((uf, index) => (
                <div key={index} className="text-gray-800">
                  • {uf}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* BT - Broader Terms */}
        {term.broader_terms && term.broader_terms.length > 0 && (
          <div className="mb-8 border-t border-gray-300 pt-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">
              <span className="text-green-600">BT</span> — {t.broaderTerms}
            </h2>
            <div className="pl-6 space-y-2">
              {term.broader_terms.map((bt, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span>•</span>
                  {renderTermLink(bt, index)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NT - Narrower Terms */}
        {term.narrower_terms && term.narrower_terms.length > 0 && (
          <div className="mb-8 border-t border-gray-300 pt-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">
              <span className="text-purple-600">NT</span> — {t.narrowerTerms}
            </h2>
            <div className="pl-6 space-y-2">
              {term.narrower_terms.map((nt, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span>•</span>
                  {renderTermLink(nt, index)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RT - Related Terms */}
        {term.related_terms && term.related_terms.length > 0 && (
          <div className="mb-8 border-t border-gray-300 pt-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">
              <span className="text-orange-600">RT</span> — {t.relatedTerms}
            </h2>
            <div className="pl-6 space-y-2">
              {term.related_terms.map((rt, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span>•</span>
                  {renderTermLink(rt, index)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Category / Microthesaurus */}
        <div className="mb-8 border-t border-gray-300 pt-6">
          <h2 className="text-lg font-bold text-gray-900 mb-3">{t.microthesaurus}</h2>
          <p className="text-gray-800 pl-6">• {term.category}</p>
        </div>
      </div>
    </div>
  );
};

export default ConceptPage;
